/** Permission keys are declared by the API and vertical modules. The server is authoritative. */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../api'
import { useAuthStore } from '../store'

export type Permission = string

interface PermissionsMeResponse {
  permissions: string[]
  tenant_wide_permissions: string[]
}

function usePermissionQuery() {
  const userId = useAuthStore((state) => state.user?.id)
  const tenantId = useAuthStore((state) => state.user?.tenant_id)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const identityKey = isAuthenticated && userId ? `${tenantId ?? ''}:${userId}` : null
  const query = useQuery({
    queryKey: ['permissions', 'me', identityKey],
    queryFn: async () => {
      const response = await api.get<PermissionsMeResponse>('/permissions/me')
      return {
        permissions: Array.isArray(response.data.permissions) ? response.data.permissions : [],
        tenant_wide_permissions: Array.isArray(response.data.tenant_wide_permissions)
          ? response.data.tenant_wide_permissions : [],
      }
    },
    enabled: Boolean(identityKey),
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  })
  return { identityKey, query }
}

/** Shared query deduplicates callers and fails closed while loading or offline. */
function usePermissionSnapshot(): PermissionsMeResponse | null {
  const { identityKey, query } = usePermissionQuery()
  return identityKey && !query.isError ? query.data ?? null : null
}

/**
 * Whether the signed-in person's permissions are known yet. Checks fail
 * closed until they are, so a screen that would say "you are not allowed"
 * should wait while ``loading`` and say the service can't be reached when
 * ``unavailable`` (a lost connection is not a missing permission).
 */
export function usePermissionsStatus(): 'loading' | 'ready' | 'unavailable' {
  const { identityKey, query } = usePermissionQuery()
  if (!identityKey) return 'ready'
  if (query.data && !query.isError) return 'ready'
  return query.isError ? 'unavailable' : 'loading'
}

export function useResolvedPermissions(): Permission[] {
  const snapshot = usePermissionSnapshot()
  return Array.isArray(snapshot?.permissions) ? snapshot.permissions : []
}

/** For screens whose server API has no record policy and requires a tenant-wide grant. */
export function useTenantWidePermission(permission: Permission): boolean {
  const snapshot = usePermissionSnapshot()
  return Array.isArray(snapshot?.tenant_wide_permissions) && snapshot.tenant_wide_permissions.includes(permission)
}

export function usePermission(permission: Permission): boolean {
  return useResolvedPermissions().includes(permission)
}

export function usePermissions(permissions: Permission[]): boolean {
  const resolved = useResolvedPermissions()
  const set = useMemo(() => new Set(resolved), [resolved])
  return permissions.every((permission) => set.has(permission))
}

export function useAllPermissions(): Permission[] {
  return useResolvedPermissions()
}

/**
 * Whether the signed-in person is the only one in their organisation who can
 * take this approving step (payment-integrity P16). They may then confirm
 * their own entries, on the confirmation screen. Fails closed: false while
 * loading, offline, or on error; the server checks again either way.
 */
export function useSoleApprover(permission: Permission): boolean {
  const userId = useAuthStore((state) => state.user?.id)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const holds = usePermission(permission)
  const query = useQuery({
    queryKey: ['permissions', 'sole-approver', userId],
    queryFn: async () => {
      const response = await api.get<{ sole: string[] }>('/permissions/me/sole-approver')
      return Array.isArray(response.data.sole) ? response.data.sole : []
    },
    enabled: Boolean(isAuthenticated && userId && holds),
    staleTime: 30_000,
    retry: false,
  })
  return holds && !query.isError && (query.data ?? []).includes(permission)
}
